/**
 * Anonymous-visitor ID lifecycle.
 *
 * On first page load we mint a UUID and persist it in localStorage as
 * `meinvent.visitorId`. The same browser keeps the same ID across
 * sessions, even if they're never signed in. We send the ID with the
 * four method calls that signal real activity (chatTurn, newsletter
 * signup, profile completion, purchase attempt) so the admin can
 * follow each visitor's journey.
 *
 * Privacy: the ID is a random UUID, NOT a fingerprint. Clearing
 * localStorage resets the visitor; a new device starts as a new
 * visitor. The user has full control.
 *
 * Robustness: every helper handles missing `localStorage` (private
 * browsing in some configs, server-side rendering paths) and
 * malformed cached values by minting a fresh ID. Never throws.
 */

const STORAGE_KEY = 'meinvent.visitorId';

/**
 * Returns the current visitor's ID, generating + persisting a new
 * one on first call. Idempotent — subsequent calls return the same
 * ID for the lifetime of the localStorage entry.
 *
 * Returns null only when the browser truly can't store anything
 * (extremely rare: certain embedded webviews). Callers should treat
 * a null return as "skip tracking, no harm."
 */
export function getOrCreateVisitorId(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored && isValidVisitorId(stored)) return stored;
    const fresh = generateVisitorId();
    window.localStorage.setItem(STORAGE_KEY, fresh);
    return fresh;
  } catch {
    // localStorage unavailable (privacy mode, quota exceeded, etc.).
    // Tracking will silently no-op for this visitor; nothing else
    // breaks.
    return null;
  }
}

/**
 * Cheap shape check — matches the same length window the backend
 * accepts (8-64 chars). Defends against tampered or empty values.
 */
function isValidVisitorId(s: string): boolean {
  return typeof s === 'string' && s.length >= 8 && s.length <= 64;
}

/**
 * Generate a new UUID. We use `crypto.randomUUID()` when available
 * (modern browsers, every supported browser version we care about)
 * and fall back to a Math.random-based generator for the rare older
 * browser. The fallback isn't cryptographically random but tracking
 * doesn't need to be — collisions are vanishingly unlikely for
 * EntreDash's expected scale.
 */
function generateVisitorId(): string {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }
  // Fallback: classic UUIDv4-ish generator. Not crypto-secure; that's
  // fine for visitor correlation.
  const hex = (n: number) =>
    Math.floor(Math.random() * Math.pow(16, n))
      .toString(16)
      .padStart(n, '0');
  return `${hex(8)}-${hex(4)}-4${hex(3)}-${'89ab'[Math.floor(Math.random() * 4)]}${hex(3)}-${hex(12)}`;
}
